import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Quote, 
  Check, 
  Image as ImageIcon, 
  Video, 
  Layout,
  Loader2,
  ChevronDown
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  if (url.startsWith("/uploads")) return `${API}${url}`;
  return url;
};

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

// Section Components
const HeroSection = ({ content }) => (
  <section
    className="relative min-h-[60vh] flex items-center justify-center overflow-hidden"
    style={{
      backgroundImage: content.background_image ? `url(${getImageUrl(content.background_image)})` : 'linear-gradient(135deg, #1A1A1A, #0A0A0A)',
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    }}
  >
    <div className="absolute inset-0 bg-[#050505]" style={{ opacity: (content.overlay_opacity || 60) / 100 }} />
    
    {/* Decorative elements */}
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute top-20 left-10 w-72 h-72 bg-[#D4AF37]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl" />
    </div>

    <motion.div 
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="relative z-10 text-center px-6 max-w-4xl mx-auto"
    >
      <motion.h1 
        variants={fadeInUp}
        className="text-4xl md:text-6xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-6"
      >
        {content.title || "Page Title"}
      </motion.h1>
      {content.subtitle && (
        <motion.p 
          variants={fadeInUp}
          className="text-xl md:text-2xl text-[#A1A1AA] mb-8"
        >
          {content.subtitle}
        </motion.p>
      )}
      {content.cta_text && content.cta_link && (
        <motion.a
          variants={fadeInUp}
          href={content.cta_link}
          className="inline-block px-8 py-4 bg-[#D4AF37] text-[#050505] font-semibold hover:bg-[#B8962E] transition-colors"
        >
          {content.cta_text}
        </motion.a>
      )}
    </motion.div>

    {/* Scroll indicator */}
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1 }}
      className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
    >
      <div className="w-6 h-10 border-2 border-[#D4AF37]/50 rounded-full flex justify-center">
        <motion.div
          animate={{ y: [0, 12, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-1.5 h-3 bg-[#D4AF37] rounded-full mt-2"
        />
      </div>
    </motion.div>
  </section>
);

const TextSection = ({ content }) => {
  const alignmentClass = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right'
  }[content.alignment || 'left'];

  return (
    <motion.section 
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={staggerContainer}
      className={`py-16 md:py-24 px-6 ${alignmentClass}`}
    >
      <div className="max-w-4xl mx-auto">
        {content.heading && (
          <motion.h2 
            variants={fadeInUp}
            className="text-3xl md:text-4xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-4"
          >
            {content.heading}
          </motion.h2>
        )}
        {content.subheading && (
          <motion.p 
            variants={fadeInUp}
            className="text-[#D4AF37] uppercase tracking-wider text-sm mb-6"
          >
            {content.subheading}
          </motion.p>
        )}
        <motion.div 
          variants={fadeInUp}
          className="prose prose-lg prose-invert max-w-none text-[#A1A1AA] leading-relaxed
            [&>p]:mb-4 [&>p]:text-[#A1A1AA]
            [&>ul]:list-disc [&>ul]:pl-6
            [&>ol]:list-decimal [&>ol]:pl-6
            [&>h3]:text-[#F5F5F0] [&>h3]:font-semibold [&>h3]:mt-6 [&>h3]:mb-3
            [&>a]:text-[#D4AF37] [&>a]:hover:underline"
          dangerouslySetInnerHTML={{ __html: content.text || "" }}
        />
      </div>
    </motion.section>
  );
};

const ImageSection = ({ content }) => {
  const sizeClasses = {
    small: "max-w-md",
    medium: "max-w-2xl",
    large: "max-w-5xl",
    full: "max-w-full"
  };
  const alignClasses = {
    left: "mr-auto",
    center: "mx-auto",
    right: "ml-auto"
  };

  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={fadeInUp}
      className="py-12 px-6"
    >
      <div className={`${sizeClasses[content.size || 'large']} ${alignClasses[content.alignment || 'center']}`}>
        {content.image ? (
          <div className="relative group">
            <div className="absolute -inset-2 bg-gradient-to-br from-[#D4AF37]/10 to-transparent blur-lg opacity-0 group-hover:opacity-100 transition-opacity" />
            <img 
              src={getImageUrl(content.image)} 
              alt={content.alt || ""} 
              className="relative w-full border border-[rgba(212,175,55,0.2)]"
            />
          </div>
        ) : (
          <div className="w-full h-64 bg-[#1A1A1A] flex items-center justify-center border border-[rgba(212,175,55,0.2)]">
            <ImageIcon className="w-12 h-12 text-[#52525B]" />
          </div>
        )}
        {content.caption && (
          <p className="text-center text-[#A1A1AA] text-sm mt-4 italic">{content.caption}</p>
        )}
      </div>
    </motion.section>
  );
};

const DividerSection = ({ content }) => {
  const widthClasses = { 
    small: "w-1/4", 
    medium: "w-1/2", 
    large: "w-3/4", 
    full: "w-full" 
  };

  const renderDivider = () => {
    switch (content.style) {
      case "dots":
        return (
          <div className="flex justify-center gap-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="w-2 h-2 rounded-full bg-[#D4AF37]" />
            ))}
          </div>
        );
      case "gradient":
        return (
          <div className={`h-px ${widthClasses[content.width || 'medium']} mx-auto bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent`} />
        );
      case "diamond":
        return (
          <div className="flex justify-center items-center gap-4">
            <div className="w-20 h-px bg-gradient-to-r from-transparent to-[#D4AF37]" />
            <div className="w-3 h-3 rotate-45 bg-[#D4AF37]" />
            <div className="w-20 h-px bg-gradient-to-l from-transparent to-[#D4AF37]" />
          </div>
        );
      case "wave":
        return (
          <div className={`${widthClasses[content.width || 'medium']} mx-auto text-center text-[#D4AF37] text-2xl tracking-widest`}>
            ～～～
          </div>
        );
      case "space":
        return null;
      default:
        return (
          <div className={`h-px ${widthClasses[content.width || 'medium']} mx-auto bg-[rgba(212,175,55,0.3)]`} />
        );
    }
  };

  return (
    <div style={{ padding: `${(content.spacing || 40) / 2}px 0` }}>
      {renderDivider()}
    </div>
  );
};

const TwoColumnSection = ({ content }) => {
  const isImageLeft = content.layout === 'image_text';

  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={staggerContainer}
      className="py-16 md:py-24 px-6"
    >
      <div className="max-w-7xl mx-auto">
        {content.title && (
          <motion.h2 
            variants={fadeInUp}
            className="text-3xl md:text-4xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-12 text-center"
          >
            {content.title}
          </motion.h2>
        )}
        <div className={`grid lg:grid-cols-2 gap-12 items-center ${isImageLeft ? '' : ''}`}>
          {/* Text Column */}
          <motion.div 
            variants={fadeInUp}
            className={`prose prose-lg prose-invert max-w-none text-[#A1A1AA] ${isImageLeft ? 'lg:order-2' : ''}`}
            dangerouslySetInnerHTML={{ __html: content.left_content || content.right_content || "" }}
          />
          
          {/* Image/Text Column */}
          <motion.div variants={fadeInUp} className={isImageLeft ? 'lg:order-1' : ''}>
            {content.layout === 'text_text' ? (
              <div 
                className="prose prose-lg prose-invert max-w-none text-[#A1A1AA]"
                dangerouslySetInnerHTML={{ __html: content.right_content || "" }}
              />
            ) : (
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#D4AF37]/20 to-transparent blur-xl" />
                {content.image ? (
                  <img 
                    src={getImageUrl(content.image)} 
                    alt="" 
                    className="relative w-full border border-[rgba(212,175,55,0.2)]"
                  />
                ) : (
                  <div className="relative w-full h-80 bg-[#1A1A1A] flex items-center justify-center border border-[rgba(212,175,55,0.2)]">
                    <ImageIcon className="w-12 h-12 text-[#52525B]" />
                  </div>
                )}
                <div className="absolute -bottom-4 -right-4 w-32 h-32 border-2 border-[#D4AF37]/30 -z-10" />
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

const ThreeColumnSection = ({ content }) => (
  <motion.section
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-100px" }}
    variants={staggerContainer}
    className="py-16 md:py-24 px-6 bg-[#0A0A0A]"
  >
    <div className="max-w-7xl mx-auto">
      {content.title && (
        <motion.h2 
          variants={fadeInUp}
          className="text-3xl md:text-4xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-12 text-center"
        >
          {content.title}
        </motion.h2>
      )}
      <div className="grid md:grid-cols-3 gap-8">
        {[1, 2, 3].map((col) => (
          <motion.div 
            key={col} 
            variants={fadeInUp}
            className="text-center p-6 border border-[rgba(212,175,55,0.1)] hover:border-[rgba(212,175,55,0.3)] transition-colors"
          >
            {content[`col${col}_image`] ? (
              <img 
                src={getImageUrl(content[`col${col}_image`])} 
                alt="" 
                className="w-20 h-20 mx-auto mb-6 object-cover rounded-full border-2 border-[#D4AF37]/30"
              />
            ) : (
              <div className="w-20 h-20 mx-auto mb-6 bg-[#1A1A1A] rounded-full flex items-center justify-center border-2 border-[#D4AF37]/30">
                <Layout className="w-8 h-8 text-[#D4AF37]" />
              </div>
            )}
            <h3 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0] mb-3">
              {content[`col${col}_title`] || `Feature ${col}`}
            </h3>
            <p className="text-[#A1A1AA] leading-relaxed">
              {content[`col${col}_text`] || "Description goes here"}
            </p>
          </motion.div>
        ))}
      </div>
    </div>
  </motion.section>
);

const CTASection = ({ content }) => (
  <motion.section
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true }}
    variants={fadeInUp}
    className={`py-20 px-6 ${
      content.style === 'gradient' 
        ? 'bg-gradient-to-r from-[#0A0A0A] via-[#D4AF37]/10 to-[#0A0A0A]' 
        : 'bg-[#0A0A0A]'
    }`}
  >
    <div className="max-w-3xl mx-auto text-center">
      <h2 className="text-3xl md:text-4xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-4">
        {content.heading || "Ready to Get Started?"}
      </h2>
      <p className="text-lg text-[#A1A1AA] mb-8">
        {content.subtext || "Contact us today to learn more"}
      </p>
      {content.button_text && (
        <a
          href={content.button_link || "#"}
          className={`inline-block px-10 py-4 font-semibold transition-all duration-300 ${
            content.style === 'outline'
              ? 'border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#050505]'
              : 'bg-[#D4AF37] text-[#050505] hover:bg-[#B8962E] hover:scale-105'
          }`}
        >
          {content.button_text}
        </a>
      )}
    </div>
  </motion.section>
);

const TestimonialSection = ({ content }) => (
  <motion.section
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true }}
    variants={fadeInUp}
    className="py-16 md:py-24 px-6"
  >
    <div className="max-w-4xl mx-auto text-center">
      {/* Stars */}
      <div className="flex justify-center mb-6">
        {[...Array(content.rating || 5)].map((_, i) => (
          <span key={i} className="text-[#D4AF37] text-2xl">★</span>
        ))}
      </div>
      
      {/* Quote */}
      <blockquote className="text-2xl md:text-3xl italic text-[#F5F5F0] mb-8 leading-relaxed">
        "{content.quote || "An amazing experience that exceeded all expectations."}"
      </blockquote>
      
      {/* Author */}
      <div className="flex items-center justify-center gap-4">
        {content.photo ? (
          <img 
            src={getImageUrl(content.photo)} 
            alt={content.author} 
            className="w-16 h-16 rounded-full object-cover border-2 border-[#D4AF37]"
          />
        ) : (
          <div className="w-16 h-16 rounded-full bg-[#1A1A1A] flex items-center justify-center border-2 border-[#D4AF37]">
            <Quote className="w-8 h-8 text-[#D4AF37]" />
          </div>
        )}
        <div className="text-left">
          <p className="font-semibold text-[#F5F5F0] text-lg">{content.author || "Happy Client"}</p>
          <p className="text-[#A1A1AA]">{content.role || "Customer"}</p>
        </div>
      </div>
    </div>
  </motion.section>
);

const VideoSection = ({ content }) => {
  const getEmbedUrl = (url) => {
    if (!url) return null;
    if (url.includes("youtube.com/watch")) {
      const id = url.split("v=")[1]?.split("&")[0];
      return `https://www.youtube.com/embed/${id}`;
    }
    if (url.includes("youtu.be")) {
      const id = url.split("youtu.be/")[1]?.split("?")[0];
      return `https://www.youtube.com/embed/${id}`;
    }
    if (url.includes("vimeo.com")) {
      const id = url.split("vimeo.com/")[1]?.split("?")[0];
      return `https://player.vimeo.com/video/${id}`;
    }
    return url;
  };

  const aspectClasses = { 
    "16:9": "aspect-video", 
    "4:3": "aspect-[4/3]", 
    "1:1": "aspect-square" 
  };

  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={fadeInUp}
      className="py-12 px-6"
    >
      <div className="max-w-5xl mx-auto">
        {content.title && (
          <h3 className="text-2xl md:text-3xl font-['Playfair_Display'] font-semibold text-[#F5F5F0] mb-6 text-center">
            {content.title}
          </h3>
        )}
        <div className={`${aspectClasses[content.aspect || '16:9']} bg-[#1A1A1A] overflow-hidden border border-[rgba(212,175,55,0.2)]`}>
          {getEmbedUrl(content.url) ? (
            <iframe 
              src={getEmbedUrl(content.url)} 
              className="w-full h-full" 
              allowFullScreen
              title={content.title || "Video"}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Video className="w-16 h-16 text-[#52525B]" />
            </div>
          )}
        </div>
      </div>
    </motion.section>
  );
};

const GallerySection = ({ content }) => {
  const images = content.images || [];
  const cols = content.columns || 3;

  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={staggerContainer}
      className="py-16 md:py-24 px-6"
    >
      <div className="max-w-7xl mx-auto">
        {content.title && (
          <motion.h2 
            variants={fadeInUp}
            className="text-3xl md:text-4xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-12 text-center"
          >
            {content.title}
          </motion.h2>
        )}
        <div 
          className="grid gap-4"
          style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
        >
          {images.map((img, i) => (
            <motion.div 
              key={i} 
              variants={fadeInUp}
              className="aspect-square bg-[#1A1A1A] overflow-hidden group cursor-pointer"
            >
              {img.url ? (
                <img 
                  src={getImageUrl(img.url)} 
                  alt={img.caption || ""} 
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center border border-[rgba(212,175,55,0.2)]">
                  <ImageIcon className="w-8 h-8 text-[#52525B]" />
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>
  );
};

const ContactFormSection = ({ content }) => {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/contact`, formData);
      setSubmitted(true);
      setFormData({ name: "", email: "", message: "" });
    } catch (error) {
      console.error("Contact form error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={fadeInUp}
      className="py-16 md:py-24 px-6"
    >
      <div className="max-w-xl mx-auto">
        {content.title && (
          <h2 className="text-3xl md:text-4xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-2 text-center">
            {content.title}
          </h2>
        )}
        {content.subtitle && (
          <p className="text-[#A1A1AA] mb-8 text-center">{content.subtitle}</p>
        )}
        
        {submitted ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
              <Check className="w-8 h-8 text-[#D4AF37]" />
            </div>
            <h3 className="text-xl font-semibold text-[#F5F5F0] mb-2">Message Sent!</h3>
            <p className="text-[#A1A1AA]">We'll get back to you shortly.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="text"
              placeholder="Your Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-4 focus:border-[#D4AF37] focus:outline-none transition-colors"
            />
            <input
              type="email"
              placeholder="Your Email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-4 focus:border-[#D4AF37] focus:outline-none transition-colors"
            />
            <textarea
              placeholder="Your Message"
              rows={5}
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              required
              className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-4 focus:border-[#D4AF37] focus:outline-none transition-colors resize-none"
            />
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-4 bg-[#D4AF37] text-[#050505] font-semibold hover:bg-[#B8962E] transition-colors disabled:opacity-50"
            >
              {submitting ? "Sending..." : (content.button_text || "Send Message")}
            </button>
          </form>
        )}
      </div>
    </motion.section>
  );
};

const PricingSection = ({ content }) => {
  const plans = content.plans || [];

  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={staggerContainer}
      className="py-16 md:py-24 px-6 bg-[#0A0A0A]"
    >
      <div className="max-w-7xl mx-auto">
        {content.title && (
          <motion.h2 
            variants={fadeInUp}
            className="text-3xl md:text-4xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-12 text-center"
          >
            {content.title}
          </motion.h2>
        )}
        <div 
          className="grid gap-8"
          style={{ gridTemplateColumns: `repeat(${Math.min(plans.length || 3, 3)}, 1fr)` }}
        >
          {plans.map((plan, i) => (
            <motion.div
              key={i}
              variants={fadeInUp}
              className={`p-8 border transition-all duration-300 hover:scale-105 ${
                plan.highlighted 
                  ? 'border-[#D4AF37] bg-gradient-to-b from-[#D4AF37]/10 to-transparent' 
                  : 'border-[rgba(212,175,55,0.2)] hover:border-[rgba(212,175,55,0.5)]'
              }`}
            >
              {plan.highlighted && (
                <span className="text-xs uppercase tracking-wider text-[#D4AF37] font-semibold">Most Popular</span>
              )}
              <h3 className="text-2xl font-['Playfair_Display'] font-semibold text-[#F5F5F0] mt-2 mb-2">
                {plan.name || "Plan Name"}
              </h3>
              <p className="text-4xl font-bold text-[#D4AF37] mb-6">
                {plan.price || "$99"}
              </p>
              <ul className="space-y-3 mb-8">
                {(plan.features || []).map((feature, j) => (
                  <li key={j} className="flex items-start gap-3 text-[#A1A1AA]">
                    <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <a
                href="/contact"
                className={`block w-full py-3 text-center font-semibold transition-colors ${
                  plan.highlighted
                    ? 'bg-[#D4AF37] text-[#050505] hover:bg-[#B8962E]'
                    : 'border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#050505]'
                }`}
              >
                Get Started
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>
  );
};

// Spacer Section
const SpacerSection = ({ content }) => (
  <div style={{ height: `${content.height || 60}px` }} className="relative">
    {content.showLine && (
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-1/2 h-px bg-[rgba(212,175,55,0.3)]" />
    )}
  </div>
);

// FAQ Section
const FAQSection = ({ content }) => {
  const [openIndex, setOpenIndex] = useState(null);
  const items = content.items || [];

  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={staggerContainer}
      className="py-16 md:py-24 px-6"
    >
      <div className="max-w-3xl mx-auto">
        {content.title && (
          <motion.h2 
            variants={fadeInUp}
            className="text-3xl md:text-4xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-12 text-center"
          >
            {content.title}
          </motion.h2>
        )}
        <div className="space-y-4">
          {items.map((item, i) => (
            <motion.div 
              key={i} 
              variants={fadeInUp}
              className="border border-[rgba(212,175,55,0.2)] overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full p-5 flex items-center justify-between text-left hover:bg-[rgba(212,175,55,0.05)] transition-colors"
              >
                <span className="font-semibold text-[#F5F5F0]">{item.question}</span>
                <ChevronDown className={`w-5 h-5 text-[#D4AF37] transition-transform ${openIndex === i ? 'rotate-180' : ''}`} />
              </button>
              <motion.div
                initial={false}
                animate={{ height: openIndex === i ? "auto" : 0, opacity: openIndex === i ? 1 : 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <p className="px-5 pb-5 text-[#A1A1AA] leading-relaxed">{item.answer}</p>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>
  );
};

// Team Section
const TeamSection = ({ content }) => {
  const members = content.members || [];
  const cols = content.columns || 3;

  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={staggerContainer}
      className="py-16 md:py-24 px-6"
    >
      <div className="max-w-7xl mx-auto">
        {content.title && (
          <motion.h2 
            variants={fadeInUp}
            className="text-3xl md:text-4xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-12 text-center"
          >
            {content.title}
          </motion.h2>
        )}
        <div 
          className="grid gap-8"
          style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
        >
          {members.map((member, i) => (
            <motion.div 
              key={i} 
              variants={fadeInUp}
              className="text-center group"
            >
              <div className="relative w-40 h-40 mx-auto mb-6 overflow-hidden rounded-full">
                <div className="absolute inset-0 border-2 border-[#D4AF37]/30 rounded-full" />
                {member.image ? (
                  <img 
                    src={getImageUrl(member.image)} 
                    alt={member.name}
                    className="w-full h-full object-cover transition-transform group-hover:scale-110"
                  />
                ) : (
                  <div className="w-full h-full bg-[#1A1A1A] flex items-center justify-center">
                    <span className="text-4xl text-[#D4AF37] font-['Playfair_Display']">
                      {(member.name || "?")[0]}
                    </span>
                  </div>
                )}
              </div>
              <h3 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0] mb-1">
                {member.name}
              </h3>
              <p className="text-[#D4AF37]">{member.role}</p>
              {member.bio && (
                <p className="text-[#A1A1AA] text-sm mt-3">{member.bio}</p>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>
  );
};

// Features Section
const FeaturesSection = ({ content }) => {
  const features = content.features || [];
  const cols = content.columns || 3;

  const iconMap = {
    star: "★",
    check: "✓",
    heart: "♥",
    award: "🏆",
    zap: "⚡",
    shield: "🛡",
    clock: "⏰",
    users: "👥"
  };

  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={staggerContainer}
      className="py-16 md:py-24 px-6 bg-[#0A0A0A]"
    >
      <div className="max-w-7xl mx-auto">
        {content.title && (
          <motion.h2 
            variants={fadeInUp}
            className="text-3xl md:text-4xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-12 text-center"
          >
            {content.title}
          </motion.h2>
        )}
        <div 
          className="grid gap-8"
          style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
        >
          {features.map((feature, i) => (
            <motion.div 
              key={i} 
              variants={fadeInUp}
              className="text-center p-8 border border-[rgba(212,175,55,0.1)] hover:border-[rgba(212,175,55,0.3)] transition-colors group"
            >
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#D4AF37]/10 flex items-center justify-center group-hover:bg-[#D4AF37]/20 transition-colors">
                <span className="text-2xl text-[#D4AF37]">{iconMap[feature.icon] || "★"}</span>
              </div>
              <h3 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0] mb-3">
                {feature.title}
              </h3>
              <p className="text-[#A1A1AA] leading-relaxed">{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>
  );
};

// Stats Section
const StatsSection = ({ content }) => {
  const stats = content.stats || [];

  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={staggerContainer}
      className="py-16 md:py-24 px-6 bg-gradient-to-r from-[#0A0A0A] via-[#D4AF37]/5 to-[#0A0A0A]"
    >
      <div className="max-w-6xl mx-auto">
        {content.title && (
          <motion.h2 
            variants={fadeInUp}
            className="text-3xl md:text-4xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-12 text-center"
          >
            {content.title}
          </motion.h2>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, i) => (
            <motion.div 
              key={i} 
              variants={fadeInUp}
              className="text-center"
            >
              <div className="text-5xl md:text-6xl font-bold text-[#D4AF37] mb-2">
                {stat.value}{stat.suffix}
              </div>
              <p className="text-[#A1A1AA] uppercase tracking-wider text-sm">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>
  );
};

// Countdown Section
const CountdownSection = ({ content }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    if (!content.targetDate) return;
    
    const target = new Date(content.targetDate).getTime();
    
    const updateCountdown = () => {
      const now = Date.now();
      const diff = target - now;
      
      if (diff <= 0) {
        setExpired(true);
        return;
      }
      
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000)
      });
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [content.targetDate]);

  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={fadeInUp}
      className="py-16 md:py-24 px-6"
    >
      <div className="max-w-3xl mx-auto text-center">
        {content.title && (
          <h2 className="text-3xl md:text-4xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-10">
            {content.title}
          </h2>
        )}
        
        {expired ? (
          <p className="text-2xl text-[#D4AF37]">{content.expiredMessage || "Event has started!"}</p>
        ) : (
          <div className="flex justify-center gap-4 md:gap-6">
            {[
              { value: timeLeft.days, label: "Days" },
              { value: timeLeft.hours, label: "Hours" },
              { value: timeLeft.minutes, label: "Minutes" },
              { value: timeLeft.seconds, label: "Seconds" }
            ].map((unit, i) => (
              <div key={i} className="p-4 md:p-6 bg-[#0A0A0A] border border-[rgba(212,175,55,0.2)] min-w-[80px] md:min-w-[100px]">
                <div className="text-3xl md:text-5xl font-bold text-[#D4AF37]">
                  {String(unit.value).padStart(2, '0')}
                </div>
                <div className="text-xs md:text-sm text-[#A1A1AA] mt-2 uppercase tracking-wider">{unit.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </motion.section>
  );
};

// Testimonials Grid Section
const TestimonialsGridSection = ({ content }) => {
  const testimonials = content.testimonials || [];
  const cols = content.columns || 3;

  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={staggerContainer}
      className="py-16 md:py-24 px-6 bg-[#0A0A0A]"
    >
      <div className="max-w-7xl mx-auto">
        {content.title && (
          <motion.h2 
            variants={fadeInUp}
            className="text-3xl md:text-4xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-12 text-center"
          >
            {content.title}
          </motion.h2>
        )}
        <div 
          className="grid gap-8"
          style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
        >
          {testimonials.map((t, i) => (
            <motion.div 
              key={i} 
              variants={fadeInUp}
              className="p-8 border border-[rgba(212,175,55,0.15)] hover:border-[rgba(212,175,55,0.3)] transition-colors"
            >
              <Quote className="w-8 h-8 text-[#D4AF37] mb-4 opacity-50" />
              <p className="text-[#F5F5F0] italic mb-6 leading-relaxed">"{t.quote}"</p>
              <div className="flex items-center gap-4">
                {t.authorImage ? (
                  <img 
                    src={getImageUrl(t.authorImage)} 
                    alt={t.authorName}
                    className="w-12 h-12 rounded-full object-cover border border-[#D4AF37]/30"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-[#1A1A1A] flex items-center justify-center border border-[#D4AF37]/30">
                    <span className="text-[#D4AF37] font-semibold">{(t.authorName || "?")[0]}</span>
                  </div>
                )}
                <div>
                  <p className="font-semibold text-[#D4AF37]">{t.authorName}</p>
                  <p className="text-[#A1A1AA] text-sm">{t.authorTitle}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>
  );
};

// Section renderer map
const SECTION_RENDERERS = {
  hero: HeroSection,
  text: TextSection,
  image: ImageSection,
  divider: DividerSection,
  spacer: SpacerSection,
  two_column: TwoColumnSection,
  three_column: ThreeColumnSection,
  cta: CTASection,
  testimonial: TestimonialSection,
  testimonials_grid: TestimonialsGridSection,
  faq: FAQSection,
  team: TeamSection,
  features: FeaturesSection,
  stats: StatsSection,
  countdown: CountdownSection,
  video: VideoSection,
  gallery: GallerySection,
  contact_form: ContactFormSection,
  pricing: PricingSection,
};

// Main Dynamic Page Component
const DynamicPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const res = await axios.get(`${API}/api/public/page/${slug}`);
        setPage(res.data);
        // Update page title
        document.title = `${res.data.title} | Mix & Mingle`;
      } catch (err) {
        setError(err.response?.status === 404 ? "Page not found" : "Failed to load page");
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-4">
            {error || "Page Not Found"}
          </h1>
          <p className="text-[#A1A1AA] mb-8">The page you're looking for doesn't exist.</p>
          <a
            href="/"
            className="inline-block px-8 py-3 bg-[#D4AF37] text-[#050505] font-semibold hover:bg-[#B8962E] transition-colors"
          >
            Go Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid={`page-${slug}`}>
      {page.sections?.map((section) => {
        const Renderer = SECTION_RENDERERS[section.type];
        return Renderer ? (
          <Renderer key={section.id} content={section.content} />
        ) : null;
      })}
    </div>
  );
};

export default DynamicPage;
